import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getReportingTimeZone, subscribeReportingTimeZone } from '@/lib/report-timezone';
import { Calendar } from './calendar';
import { MonthPanel, QuarterPanel, YearPanel } from './panels';
import { TimePicker } from './time-picker';
import { defaultPresets } from './presets';
import { disableContextFrom, isDayDisabled, isHoliday, isMonthDisabled } from './constraints';
import {
  addDays,
  type Civil,
  civil,
  civilFromInstant,
  compareDate,
  diffDays,
  formatCivil,
  formatCompactRange,
  hmToMinutes,
  instantFromCivil,
  monthNames,
  parseCivil,
  quarterOf,
  startOfWeek,
} from './date-utils';
import type { DateRange, DatePickerBaseProps, DatePickerPreset, DatePickerValue } from './types';

// ── small internal helpers ───────────────────────────────────────────────────

type Pair = { start: Civil | null; end: Civil | null };

const RANGE_MODES = new Set(['range', 'datetime-range']);
const TIME_MODES = new Set(['datetime', 'datetime-range']);

function defaultFormat(mode: string, use12: boolean, showSeconds: boolean): string {
  const time = use12 ? (showSeconds ? 'hh:mm:ss A' : 'hh:mm A') : showSeconds ? 'HH:mm:ss' : 'HH:mm';
  switch (mode) {
    case 'datetime':
    case 'datetime-range':
      return `MM/DD/YYYY ${time}`;
    case 'month':
    case 'month-year':
      return 'MMM YYYY';
    case 'year':
      return 'YYYY';
    default:
      return 'MM/DD/YYYY';
  }
}

/** Subscribe to the app reporting zone without coupling to react-query. */
function useReportingZone(): string {
  return useSyncExternalStore(subscribeReportingTimeZone, getReportingTimeZone, getReportingTimeZone);
}

// ── component ─────────────────────────────────────────────────────────────────

/**
 * DatePicker — the single reusable date/time selection component. Switch scenario
 * via `mode`; range modes exchange {@link DateRange}, all others `Date | null`.
 * Timezone-correct (DST-safe) via the app's reporting-zone engine, fully
 * keyboard-accessible, controlled or uncontrolled.
 */
export const DatePicker = memo(function DatePicker(props: DatePickerBaseProps) {
  const {
    mode = 'date',
    value,
    defaultValue,
    onChange,
    onApply,
    format,
    compactDisplay,
    placeholder,
    label,
    className,
    popoverClassName,
    disabled,
    readOnly,
    required,
    clearable = true,
    allowManualInput,
    autoApply: autoApplyProp,
    portal = false,
    showSeconds = false,
    use12Hour = false,
    timeVariant = 'stepper',
    minuteStep = 1,
    timezone,
    minTime,
    maxTime,
    maxRangeDays,
    minRangeDays,
    presets,
    presetFirst = false,
    optionalTime = false,
    footer,
    firstDayOfWeek = 0,
    locale = 'en-US',
    dir = 'ltr',
    yearRange,
    error,
    errorMessage,
    name,
    id,
    renderTrigger,
  } = props;

  const isRange = RANGE_MODES.has(mode);
  const hasTime = TIME_MODES.has(mode);
  const autoApply = autoApplyProp ?? !hasTime;
  const canType = allowManualInput ?? (mode === 'date' || mode === 'datetime');

  const zoneFromApp = useReportingZone();
  const tz = timezone || zoneFromApp;

  const fmt = format ?? defaultFormat(mode, use12Hour, showSeconds);

  const footerCfg = {
    today: footer?.today ?? true,
    clear: footer?.clear ?? clearable,
    cancel: footer?.cancel ?? !autoApply,
    apply: footer?.apply ?? !autoApply,
  };

  // ── committed value (controlled or uncontrolled) ───────────────────────────
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<DatePickerValue>(defaultValue ?? null);
  const committed = isControlled ? value : internalValue;

  const committedPair = useMemo<Pair>(() => valueToPair(committed ?? null, isRange, tz), [committed, isRange, tz]);

  // `optionalTime` is offered only where the mode has no time of its own.
  const canOfferTime = optionalTime && !hasTime && !['month', 'month-year', 'year', 'quarter'].includes(mode);

  /** A committed value carries a *meaningful* time once it stops being the
   *  whole-day span (00:00 → 23:59). Derived rather than stored so the trigger
   *  keeps showing times after the popover closes and across remounts. */
  const committedHasTime = useMemo(() => {
    if (!canOfferTime) return false;
    const s = committedPair.start;
    const e = committedPair.end;
    if (s && (s.hour !== 0 || s.minute !== 0)) return true;
    if (isRange && e && (e.hour !== 23 || e.minute !== 59)) return true;
    return false;
  }, [canOfferTime, committedPair, isRange]);

  // ── popup + working state ──────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Pair>(committedPair);
  const [view, setView] = useState<{ year: number; month: number }>(() => initialView(committedPair, tz));
  const [yearPage, setYearPage] = useState<number>(
    () => Math.floor((committedPair.start?.year ?? todayCivil(tz).year) / 12) * 12,
  );
  const [focus, setFocus] = useState<Civil>(() => committedPair.start ?? todayCivil(tz));
  const [hover, setHover] = useState<Civil | null>(null);
  const [inputText, setInputText] = useState<string | null>(null);
  // Preset-first only: which face of the popover is showing. Always reset to
  // 'list' when the popover opens so the calendar is never the landing view.
  const [presetView, setPresetView] = useState<'list' | 'calendar'>('list');
  // `optionalTime` only: has the user ticked "Set specific times" this session?
  const [timeOn, setTimeOn] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  // Fixed-position coords for the portaled popup (only used when `portal`).
  const [popupPos, setPopupPos] = useState<{
    left: number;
    top: number;
    minWidth: number;
    maxHeight: number;
    maxWidth: number;
  } | null>(null);

  // Position the portaled popup against the trigger, flipping above when there
  // isn't room below. Recomputed on open and while scrolling/resizing so it
  // stays glued to the field (e.g. when the modal body scrolls).
  //
  // Also caps the height to whichever side it lands on. Without the cap a tall
  // popover (two-month range calendar + time row + footer) runs off the bottom
  // of the viewport, and on a short page there is nothing to scroll to reach
  // the Apply button — it is simply unclickable. With the cap the popover
  // scrolls internally instead and the footer stays pinned.
  const repositionPopup = useCallback(() => {
    const anchor = containerRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const gap = 8;
    const popH = popupRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const flipUp = popH > 0 && spaceBelow < popH && spaceAbove > spaceBelow;
    // Never shrink below a usable calendar; the popover scrolls past that.
    const room = Math.max(260, (flipUp ? spaceAbove : spaceBelow) - gap);
    const top = flipUp ? Math.max(gap, r.top - gap - Math.min(popH, room)) : r.bottom + gap;
    // Clamp against the *final* top: `room` is measured from the trigger, but a
    // flip (or a clamped top) moves the popover, so the two must agree or the
    // panel still spills past the bottom edge.
    const maxHeight = Math.max(200, Math.min(room, window.innerHeight - top - gap));

    // Keep it inside the viewport horizontally too. A two-month range calendar
    // is far wider than its trigger, so left-anchoring a field near the right
    // edge pushes Apply off-screen — same unclickable-footer bug, sideways.
    const maxWidth = window.innerWidth - gap * 2;
    const popW = Math.min(popupRef.current?.offsetWidth ?? 0, maxWidth);
    const left = Math.max(gap, Math.min(r.left, window.innerWidth - popW - gap));

    setPopupPos({ left, top, minWidth: r.width, maxHeight, maxWidth });
  }, []);

  // `presetView` / `timeOn` change the popover's height dramatically (preset
  // list ↔ two-month calendar ↔ calendar + time rows), so re-measure on each.
  useLayoutEffect(() => {
    if (!open || !portal) return;
    repositionPopup();
    // A second pass once the new content has painted: the first call measures
    // the *previous* height (or 0 on open), which is what decides the flip.
    const raf = requestAnimationFrame(repositionPopup);
    const onScrollOrResize = () => repositionPopup();
    // Capture phase so a scrolling ancestor (modal body) also triggers it.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, portal, repositionPopup, presetView, timeOn]);

  const resolvedYearRange = useMemo<[number, number]>(() => {
    if (yearRange) return yearRange;
    const y = todayCivil(tz).year;
    return [y - 100, y + 10];
  }, [yearRange, tz]);

  // Re-seed the draft/view whenever the popup opens or the committed value moves.
  useEffect(() => {
    if (!open) return;
    setDraft(committedPair);
    setView(initialView(committedPair, tz));
    setFocus(committedPair.start ?? todayCivil(tz));
    setYearPage(Math.floor((committedPair.start?.year ?? todayCivil(tz).year) / 12) * 12);
    setInputText(null);
    setPresetView('list');
    setTimeOn(committedHasTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Outside-click + Escape to dismiss (mirrors SearchableSelect's behavior).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The popup may be portaled outside containerRef, so exempt it too.
      if (containerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      closeAndRevert();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeAndRevert();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── disable predicate (memoized around the constraint props) ───────────────
  const disableCtx = useMemo(() => disableContextFrom(props, tz, new Date()), [props, tz]);
  const isDisabled = useCallback((d: Civil) => isDayDisabled(d, disableCtx), [disableCtx]);
  const isMonthOff = useCallback((y: number, m: number) => isMonthDisabled(y, m, disableCtx), [disableCtx]);
  const isHol = useCallback((d: Civil) => isHoliday(d, disableCtx), [disableCtx]);

  // ── commit helpers ─────────────────────────────────────────────────────────
  const emit = useCallback(
    (pair: Pair, apply: boolean) => {
      const next = pairToValue(pair, mode, tz);
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
      if (apply) onApply?.(next);
    },
    [isControlled, mode, tz, onChange, onApply],
  );

  const closeAndRevert = useCallback(() => {
    setOpen(false);
    setHover(null);
    setInputText(null);
  }, []);

  const commitAndMaybeClose = useCallback(
    (pair: Pair, opts: { close: boolean }) => {
      setDraft(pair);
      emit(pair, true);
      if (opts.close) {
        setOpen(false);
        setHover(null);
        setInputText(null);
      }
    },
    [emit],
  );

  // ── selection handlers ─────────────────────────────────────────────────────
  const mergeTime = (day: Civil, from: Civil | null, fallbackEnd = false): Civil => ({
    ...day,
    hour: from?.hour ?? (fallbackEnd ? 23 : 0),
    minute: from?.minute ?? (fallbackEnd ? 59 : 0),
    second: from?.second ?? (fallbackEnd ? 59 : 0),
  });

  const selectDay = useCallback(
    (day: Civil) => {
      if (isDisabled(day)) return;
      setFocus(day);

      if (mode === 'week') {
        const start = startOfWeek(day, firstDayOfWeek);
        commitAndMaybeClose({ start, end: null }, { close: autoApply });
        return;
      }

      if (!isRange) {
        const start = mergeTime(day, draft.start);
        setDraft({ start, end: null });
        setInputText(null);
        if (autoApply) commitAndMaybeClose({ start, end: null }, { close: true });
        else emit({ start, end: null }, false);
        return;
      }

      // Range modes: first click sets start, second sets end (with validation).
      const haveStart = draft.start && !draft.end;
      if (!haveStart) {
        const start = mergeTime(day, draft.start);
        setDraft({ start, end: null });
        emit({ start, end: null }, false);
        return;
      }

      let a = draft.start as Civil;
      let b = mergeTime(day, draft.end, true);
      if (compareDate(b, a) < 0)
        [a, b] = [
          { ...b, hour: a.hour, minute: a.minute, second: a.second },
          { ...a, hour: b.hour, minute: b.minute, second: b.second },
        ];

      const span = diffDays(b, a) + 1;
      if (maxRangeDays && span > maxRangeDays) return;
      if (minRangeDays && span < minRangeDays) return;

      const pair = { start: a, end: b };
      setDraft(pair);
      if (autoApply) commitAndMaybeClose(pair, { close: true });
      else emit(pair, false);
    },
    [
      autoApply,
      commitAndMaybeClose,
      draft,
      emit,
      firstDayOfWeek,
      isDisabled,
      isRange,
      maxRangeDays,
      minRangeDays,
      mode,
    ],
  );

  const moveFocus = useCallback((d: Civil) => {
    setFocus(d);
    setView({ year: d.year, month: d.month });
  }, []);

  const setStartTime = useCallback(
    (next: Civil) => {
      const clamped = clampTime(next, minTime, maxTime);
      const pair = { ...draft, start: clamped };
      setDraft(pair);
      if (autoApply) emit(pair, false);
    },
    [autoApply, draft, emit, minTime, maxTime],
  );
  const setEndTime = useCallback(
    (next: Civil) => {
      const clamped = clampTime(next, minTime, maxTime);
      const pair = { ...draft, end: clamped };
      setDraft(pair);
      if (autoApply) emit(pair, false);
    },
    [autoApply, draft, emit, minTime, maxTime],
  );

  // month / year / quarter selection
  const selectMonth = useCallback(
    (year: number, month: number) => {
      const start = civil(year, month, 1);
      commitAndMaybeClose({ start, end: null }, { close: autoApply });
      setView({ year, month });
    },
    [autoApply, commitAndMaybeClose],
  );
  const selectYear = useCallback(
    (year: number) => {
      const start = civil(year, 1, 1);
      commitAndMaybeClose({ start, end: null }, { close: autoApply });
    },
    [autoApply, commitAndMaybeClose],
  );
  const selectQuarter = useCallback(
    (year: number, q: number) => {
      // q is the quarter index (1–4); its first month is (q-1)*3 + 1.
      const start = civil(year, (q - 1) * 3 + 1, 1);
      commitAndMaybeClose({ start, end: null }, { close: autoApply });
    },
    [autoApply, commitAndMaybeClose],
  );

  // ── footer actions ─────────────────────────────────────────────────────────
  const onToday = useCallback(() => {
    const t = todayCivil(tz);
    const now = civilFromInstant(new Date(), tz);
    if (isRange) {
      const start = hasTime ? now : { ...t, hour: 0, minute: 0, second: 0 };
      const end = hasTime ? now : { ...t, hour: 23, minute: 59, second: 59 };
      const pair = { start, end };
      setDraft(pair);
      setView({ year: t.year, month: t.month });
      if (autoApply) commitAndMaybeClose(pair, { close: true });
      else emit(pair, false);
    } else {
      const start = hasTime ? now : t;
      setDraft({ start, end: null });
      setFocus(start);
      setView({ year: t.year, month: t.month });
      if (autoApply) commitAndMaybeClose({ start, end: null }, { close: true });
      else emit({ start, end: null }, false);
    }
  }, [autoApply, commitAndMaybeClose, emit, hasTime, isRange, tz]);

  const onClear = useCallback(() => {
    const empty = { start: null, end: null };
    setDraft(empty);
    setInputText(null);
    emit(empty, false);
    if (autoApply) setOpen(false);
  }, [autoApply, emit]);

  const onApplyClick = useCallback(() => {
    commitAndMaybeClose(draft, { close: true });
  }, [commitAndMaybeClose, draft]);

  // ── presets ────────────────────────────────────────────────────────────────
  const presetList: DatePickerPreset[] = useMemo(() => {
    if (!presets) return [];
    return presets === true ? defaultPresets(mode) : presets;
  }, [presets, mode]);

  const applyPreset = useCallback(
    (p: DatePickerPreset) => {
      const v = p.getValue({ timezone: tz, now: new Date() });
      const pair = valueToPair(isRange ? (v as DateRange) : (v as Date), isRange, tz);
      setDraft(pair);
      if (pair.start) setView({ year: pair.start.year, month: pair.start.month });
      commitAndMaybeClose(pair, { close: true });
    },
    [commitAndMaybeClose, isRange, tz],
  );

  // Preset-first only: resolve every preset once per open so each row can show
  // the dates it stands for ("This Quarter" is meaningless without them) and so
  // the row matching the committed value can be ticked. Recomputed when the
  // popover opens rather than on every render — presets are `now`-relative and
  // re-resolving mid-interaction would make rows shift under the cursor.
  const resolvedPresets = useMemo(() => {
    if (!presetFirst || presetList.length === 0) return [];
    const now = new Date();
    return presetList.map((p) => {
      const pair = valueToPair(p.getValue({ timezone: tz, now }) as Date | DateRange, isRange, tz);
      // No `today` here on purpose: the row already says "Today"/"Yesterday",
      // and repeating it underneath reads as a stutter.
      const label = compactDisplay
        ? formatCompactRange(pair.start, isRange ? pair.end : null, { locale })
        : pair.start
          ? isRange && pair.end && compareDate(pair.start, pair.end) !== 0
            ? `${formatCivil(pair.start, 'MMM D', locale)} – ${formatCivil(pair.end, 'MMM D', locale)}`
            : formatCivil(pair.start, 'MMM D', locale)
          : '';
      return { preset: p, pair, dates: label };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetFirst, presetList, isRange, tz, locale, compactDisplay, open]);

  /** True when the committed selection is exactly this preset's range. */
  const isActivePreset = useCallback(
    (pair: Pair) => {
      if (!committedPair.start || !pair.start) return false;
      if (compareDate(committedPair.start, pair.start) !== 0) return false;
      if (!isRange) return true;
      if (!committedPair.end || !pair.end) return false;
      return compareDate(committedPair.end, pair.end) === 0;
    },
    [committedPair, isRange],
  );

  // ── manual input ───────────────────────────────────────────────────────────
  const commitTyped = useCallback(() => {
    if (inputText == null) return;
    const text = inputText.trim();
    if (!text) {
      onClear();
      setInputText(null);
      return;
    }
    const base = draft.start ?? todayCivil(tz);
    const parsed = parseCivil(text, fmt, base);
    if (parsed && !isDisabled(parsed)) {
      setDraft({ start: parsed, end: null });
      setFocus(parsed);
      setView({ year: parsed.year, month: parsed.month });
      emit({ start: parsed, end: null }, true);
    }
    setInputText(null); // revert to formatted (valid) or restore prior (invalid)
  }, [draft.start, emit, fmt, inputText, isDisabled, onClear, tz]);

  // ── display text ───────────────────────────────────────────────────────────
  // With `optionalTime`, the trigger only grows a time once the value actually
  // carries one — a whole-day range still reads "08/11/2026 – 08/17/2026".
  const displayFmt = useMemo(() => {
    if (format) return format;
    if (committedHasTime) return defaultFormat(isRange ? 'datetime-range' : 'datetime', use12Hour, showSeconds);
    return fmt;
  }, [format, committedHasTime, isRange, use12Hour, showSeconds, fmt]);

  // `compactDisplay` swaps the token format for prose ("Today", "16 Aug - 2 Sep
  // 2026") to keep a narrow trigger from clipping. Two cases opt back out:
  // fields that accept typing, where what is shown has to be what `parseCivil`
  // reads back; and a value carrying a time-of-day (an `optionalTime` range
  // whose owner ticked "Set Specific Times"), since the compact form has no
  // room for the times and hiding them would lose what they just chose.
  const compactText = compactDisplay && !canType && !committedHasTime;
  const displayText = useMemo(
    () =>
      compactText
        ? formatCompactRange(committedPair.start, committedPair.end, { locale, today: todayCivil(tz) })
        : formatPair(committedPair, mode, displayFmt, locale, firstDayOfWeek),
    [compactText, committedPair, mode, displayFmt, locale, firstDayOfWeek, tz],
  );
  const hasValue = committedPair.start != null || committedPair.end != null;

  const toggle = () => {
    if (disabled || readOnly) return;
    setOpen((o) => !o);
  };

  const today = todayCivil(tz);
  const showCalendar = !['month', 'month-year', 'year', 'quarter'].includes(mode);
  const showTwoMonths = isRange;

  /** Tick/untick "Set specific times". Unticking snaps the draft back to the
   *  whole-day span so an abandoned time edit can't leave a partial window on
   *  the report (e.g. 09:00 → 17:30 lingering after the box is cleared). */
  const toggleTime = useCallback((on: boolean) => {
    setTimeOn(on);
    if (on) return;
    setDraft((d) => ({
      start: d.start ? { ...d.start, hour: 0, minute: 0, second: 0 } : d.start,
      end: d.end ? { ...d.end, hour: 23, minute: 59, second: 59 } : d.end,
    }));
  }, []);

  // Time UI shows for genuine datetime modes, or once opted into via optionalTime.
  const showTimeUI = hasTime || (canOfferTime && timeOn);

  // Preset-first is only meaningful when there are presets AND a calendar to
  // fall back to; month/quarter/year panels have nothing to switch between.
  const usePresetFirst = presetFirst && presetList.length > 0 && showCalendar;
  const showPresetListView = usePresetFirst && presetView === 'list';
  // The old side rail is suppressed under preset-first — the list *is* the view.
  const showPresetRail = presetList.length > 0 && !usePresetFirst;

  // Live "Range: …" summary of the working selection (range modes only). Same
  // start/end collapses to a single date so a one-day pick isn't shown twice.
  const rangeSummary = isRange ? formatRangeSummary(draft, locale) : null;
  const anyFooterButton = footerCfg.today || footerCfg.clear || footerCfg.cancel || footerCfg.apply;
  const showFooter = anyFooterButton || (isRange && (draft.start != null || draft.end != null));

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className={cn('relative w-full', className)} dir={dir}>
      {renderTrigger ? (
        renderTrigger({ displayValue: displayText, open, disabled: !!disabled, clear: onClear, toggle })
      ) : (
        <div className="relative">
          {label && (
            <label
              htmlFor={id}
              className={cn(
                'pointer-events-none absolute left-9 z-10 origin-left text-muted-foreground transition-all',
                hasValue || open || inputText != null ? 'top-1 text-[10px]' : 'top-1/2 -translate-y-1/2 text-sm',
              )}
            >
              {label}
            </label>
          )}
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
          </span>
          <input
            id={id}
            name={name}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-label={props['aria-label'] ?? label}
            required={required}
            disabled={disabled}
            readOnly={!canType || readOnly}
            placeholder={placeholder ?? fmt}
            value={inputText ?? displayText}
            onChange={(e) => canType && setInputText(e.target.value)}
            // Open-only on click (never toggle) — focus does NOT open, so a
            // single click can't open-then-close. Dismissal is via outside-click,
            // Esc, or Apply/Cancel.
            onClick={() => {
              if (!disabled && !readOnly && !open) setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (inputText != null) commitTyped();
                else setOpen(true);
              }
              if (e.key === 'ArrowDown' && !open) setOpen(true);
            }}
            onBlur={() => inputText != null && commitTyped()}
            className={cn(
              'h-9 w-full rounded-lg border bg-background pl-9 pr-9 text-sm outline-none transition-colors',
              'border-input focus:border-primary focus:ring-1 focus:ring-primary',
              label && 'pt-3',
              error && 'border-destructive focus:border-destructive focus:ring-destructive',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          />
          {clearable && hasValue && !disabled && !readOnly ? (
            <button
              type="button"
              aria-label="Clear"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      )}

      {error && errorMessage && <p className="mt-1 text-xs text-destructive">{errorMessage}</p>}

      {open &&
        (() => {
          const panel = (
            <div
              ref={popupRef}
              role="dialog"
              aria-label={label ?? 'Choose date'}
              style={
                portal && popupPos
                  ? {
                      position: 'fixed',
                      left: popupPos.left,
                      top: popupPos.top,
                      minWidth: popupPos.minWidth,
                      maxHeight: popupPos.maxHeight,
                      maxWidth: popupPos.maxWidth,
                    }
                  : undefined
              }
              className={cn(
                'z-50 flex flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg sm:flex-row',
                'animate-in fade-in-0 zoom-in-95 duration-100',
                portal ? 'fixed' : cn('absolute mt-2', dir === 'rtl' ? 'right-0' : 'left-0'),
                popoverClassName,
              )}
            >
              {showPresetRail && (
                // Horizontal scroll strip on mobile (so presets survive on small
                // screens), vertical rail from `sm` up.
                <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border bg-muted/30 p-2 sm:w-36 sm:flex-col sm:gap-0.5 sm:overflow-x-visible sm:border-b-0 sm:border-r">
                  {presetList.map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}

              {showPresetListView ? (
                // ── preset-first: the list is the whole popover ──────────────
                // min-w keeps the widest row ("Last 30 Days · Jul 19 – Aug 17")
                // on one line; without it the list sizes under its content and
                // the rows pick up a horizontal scrollbar. overflow-x-hidden is
                // the belt-and-braces for locales with longer month names.
                <div className="flex min-w-[19.5rem] flex-col p-1.5" role="menu" aria-label="Date range">
                  <div className="max-h-[19rem] overflow-y-auto overflow-x-hidden">
                    {resolvedPresets.map(({ preset, pair, dates }) => {
                      const active = isActivePreset(pair);
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          role="menuitemradio"
                          aria-checked={active}
                          onClick={() => applyPreset(preset)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors',
                            active ? 'bg-primary/10 font-semibold text-primary' : 'text-foreground hover:bg-muted',
                          )}
                        >
                          <Check className={cn('h-3.5 w-3.5 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
                          <span className="whitespace-nowrap">{preset.label}</span>
                          <span
                            className={cn(
                              'ml-auto whitespace-nowrap pl-4 text-[11px] tabular-nums',
                              active ? 'text-primary' : 'text-muted-foreground',
                            )}
                          >
                            {dates}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="my-1 h-px shrink-0 bg-border" />

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => setPresetView('calendar')}
                    className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs text-foreground transition-colors hover:bg-muted"
                  >
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                    <span className="whitespace-nowrap">Custom Range…</span>
                    <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </div>
              ) : (
                // Column so the calendar body can scroll inside a capped popover
                // while the Apply/Cancel footer stays pinned and reachable.
                <div className="flex min-h-0 min-w-0 flex-col p-2.5 sm:p-3">
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {usePresetFirst && (
                      // Back out of the calendar without losing the popover — the
                      // list is where every other choice lives.
                      <button
                        type="button"
                        onClick={() => setPresetView('list')}
                        className="mb-1.5 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        Back to ranges
                      </button>
                    )}
                    {showCalendar ? (
                      <>
                        <CalendarHeader
                          view={view}
                          locale={locale}
                          yearRange={resolvedYearRange}
                          onPrev={() => setView((v) => shiftMonth(v, -1))}
                          onNext={() => setView((v) => shiftMonth(v, 1))}
                          onMonth={(m) => setView((v) => ({ ...v, month: m }))}
                          onYear={(y) => setView((v) => ({ ...v, year: y }))}
                        />
                        <div className="flex gap-4">
                          <Calendar
                            viewYear={view.year}
                            viewMonth={view.month}
                            selStart={draft.start}
                            selEnd={draft.end}
                            hover={hover}
                            focus={focus}
                            today={today}
                            isRange={isRange}
                            weekMode={mode === 'week'}
                            firstDayOfWeek={firstDayOfWeek}
                            locale={locale}
                            dir={dir}
                            isDisabled={isDisabled}
                            isHoliday={isHol}
                            onSelect={selectDay}
                            onHover={setHover}
                            onFocusChange={moveFocus}
                          />
                          {showTwoMonths && (
                            <div className="hidden sm:block">
                              <Calendar
                                viewYear={shiftMonth(view, 1).year}
                                viewMonth={shiftMonth(view, 1).month}
                                selStart={draft.start}
                                selEnd={draft.end}
                                hover={hover}
                                focus={focus}
                                today={today}
                                isRange={isRange}
                                weekMode={false}
                                firstDayOfWeek={firstDayOfWeek}
                                locale={locale}
                                dir={dir}
                                isDisabled={isDisabled}
                                isHoliday={isHol}
                                onSelect={selectDay}
                                onHover={setHover}
                                onFocusChange={moveFocus}
                              />
                            </div>
                          )}
                        </div>

                        {canOfferTime && (
                          <label className="mt-3 flex cursor-pointer select-none items-center gap-2 border-t border-border pt-3 text-xs text-foreground">
                            <input
                              type="checkbox"
                              checked={timeOn}
                              onChange={(e) => toggleTime(e.target.checked)}
                              className="h-3.5 w-3.5 cursor-pointer accent-primary"
                            />
                            Set Specific Times
                            {!timeOn && (
                              <span className="text-[11px] text-muted-foreground">
                                — whole days, 12:00 AM → 11:59 PM
                              </span>
                            )}
                          </label>
                        )}

                        {showTimeUI && (
                          <div
                            className={cn(
                              'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4',
                              // The opt-in checkbox already drew the divider above.
                              canOfferTime ? 'mt-2' : 'mt-3 border-t border-border pt-3',
                            )}
                          >
                            <div className="flex items-center gap-2">
                              {isRange && <span className="w-12 text-xs font-medium text-muted-foreground">Start</span>}
                              <TimePicker
                                value={draft.start ?? { ...today, hour: 0, minute: 0, second: 0 }}
                                onChange={setStartTime}
                                use12Hour={use12Hour}
                                showSeconds={showSeconds}
                                minuteStep={minuteStep}
                                variant={timeVariant}
                                disabled={!draft.start}
                              />
                            </div>
                            {isRange && (
                              <div className="flex items-center gap-2">
                                <span className="w-12 text-xs font-medium text-muted-foreground">End</span>
                                <TimePicker
                                  value={draft.end ?? { ...today, hour: 23, minute: 59, second: 59 }}
                                  onChange={setEndTime}
                                  use12Hour={use12Hour}
                                  showSeconds={showSeconds}
                                  minuteStep={minuteStep}
                                  variant={timeVariant}
                                  disabled={!draft.end}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : mode === 'year' ? (
                      <YearPanel
                        pageStart={yearPage}
                        selectedYear={draft.start?.year ?? null}
                        onPageChange={setYearPage}
                        onSelect={selectYear}
                        minYear={resolvedYearRange[0]}
                        maxYear={resolvedYearRange[1]}
                      />
                    ) : mode === 'quarter' ? (
                      <QuarterPanel
                        year={view.year}
                        selectedYear={draft.start?.year ?? null}
                        selectedQuarter={draft.start ? quarterOf(draft.start) : null}
                        onYearChange={(y) => setView((v) => ({ ...v, year: y }))}
                        onSelect={(q) => selectQuarter(view.year, q)}
                      />
                    ) : (
                      <MonthPanel
                        year={view.year}
                        selectedYear={draft.start?.year ?? null}
                        selectedMonth={draft.start?.month ?? null}
                        locale={locale}
                        onYearChange={(y) => setView((v) => ({ ...v, year: y }))}
                        onSelect={(m) => selectMonth(view.year, m)}
                        isMonthDisabled={isMonthOff}
                      />
                    )}
                  </div>

                  {showFooter && (
                    // Stacks on mobile (summary above the actions) and spreads onto
                    // one row from `sm` up, so Apply/Clear stay on-screen either way.
                    // shrink-0 keeps it pinned below the scrolling body.
                    <div className="mt-3 flex shrink-0 flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        {isRange ? (
                          <div className="truncate text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">Range:</span>{' '}
                            {rangeSummary ? (
                              <span className="text-foreground">{rangeSummary}</span>
                            ) : (
                              <span>Select dates</span>
                            )}
                          </div>
                        ) : footerCfg.today ? (
                          <button
                            type="button"
                            onClick={onToday}
                            className="rounded-md px-2 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10"
                          >
                            {hasTime ? 'Now' : 'Today'}
                          </button>
                        ) : null}
                      </div>
                      {(footerCfg.clear || footerCfg.cancel || footerCfg.apply) && (
                        <div className="flex items-center justify-end gap-2">
                          {footerCfg.clear && (
                            <button
                              type="button"
                              onClick={onClear}
                              className="rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
                            >
                              Clear
                            </button>
                          )}
                          {footerCfg.cancel && (
                            <button
                              type="button"
                              onClick={closeAndRevert}
                              className="rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
                            >
                              Cancel
                            </button>
                          )}
                          {footerCfg.apply && (
                            <button
                              type="button"
                              onClick={onApplyClick}
                              className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                            >
                              Apply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
          return portal ? createPortal(panel, document.body) : panel;
        })()}
    </div>
  );
});

// ── calendar header (month/year selectors + prev/next) ───────────────────────

const CalendarHeader = memo(function CalendarHeader({
  view,
  locale,
  yearRange,
  onPrev,
  onNext,
  onMonth,
  onYear,
}: {
  view: { year: number; month: number };
  locale: string;
  yearRange: [number, number];
  onPrev: () => void;
  onNext: () => void;
  onMonth: (m: number) => void;
  onYear: (y: number) => void;
}) {
  const months = monthNames(locale, 'long');
  const years = useMemo(
    () => Array.from({ length: yearRange[1] - yearRange[0] + 1 }, (_, i) => yearRange[0] + i),
    [yearRange],
  );
  const selectCls =
    'rounded-md border border-transparent bg-transparent px-1.5 py-1 text-xs font-semibold text-foreground outline-none hover:bg-muted focus:border-primary';
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <button
        type="button"
        aria-label="Previous month"
        onClick={onPrev}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-1">
        <select
          className={selectCls}
          value={view.month}
          onChange={(e) => onMonth(Number(e.target.value))}
          aria-label="Month"
        >
          {months.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </select>
        <select
          className={selectCls}
          value={view.year}
          onChange={(e) => onYear(Number(e.target.value))}
          aria-label="Year"
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        aria-label="Next month"
        onClick={onNext}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
});

// ── pure conversion / formatting helpers (module scope, testable) ────────────

function todayCivil(tz: string): Civil {
  return civilFromInstant(new Date(), tz);
}

function initialView(pair: Pair, tz: string): { year: number; month: number } {
  const c = pair.start ?? todayCivil(tz);
  return { year: c.year, month: c.month };
}

function shiftMonth(v: { year: number; month: number }, delta: number): { year: number; month: number } {
  const t = v.year * 12 + (v.month - 1) + delta;
  return { year: Math.floor(t / 12), month: (((t % 12) + 12) % 12) + 1 };
}

function valueToPair(value: DatePickerValue, isRange: boolean, tz: string): Pair {
  if (isRange) {
    const r = (value as DateRange | null) ?? null;
    return {
      start: r?.start ? civilFromInstant(r.start, tz) : null,
      end: r?.end ? civilFromInstant(r.end, tz) : null,
    };
  }
  const d = (value as Date | null) ?? null;
  return { start: d ? civilFromInstant(d, tz) : null, end: null };
}

function pairToValue(pair: Pair, mode: string, tz: string): DatePickerValue {
  if (RANGE_MODES.has(mode)) {
    return {
      start: pair.start ? instantFromCivil(pair.start, tz) : null,
      end: pair.end ? instantFromCivil(pair.end, tz) : null,
    };
  }
  return pair.start ? instantFromCivil(pair.start, tz) : null;
}

function clampTime(c: Civil, minTime?: string, maxTime?: string): Civil {
  const mins = c.hour * 60 + c.minute;
  const lo = hmToMinutes(minTime);
  const hi = hmToMinutes(maxTime);
  let m = mins;
  if (lo != null && m < lo) m = lo;
  if (hi != null && m > hi) m = hi;
  if (m === mins) return c;
  return { ...c, hour: Math.floor(m / 60), minute: m % 60 };
}

function formatSingle(c: Civil, mode: string, fmt: string, locale: string, firstDayOfWeek: number): string {
  switch (mode) {
    case 'year':
      return String(c.year);
    case 'quarter':
      return `Q${quarterOf(c)} ${c.year}`;
    case 'week': {
      const s = startOfWeek(c, firstDayOfWeek);
      const e = addDays(s, 6);
      return `${formatCivil(s, 'MMM D', locale)} – ${formatCivil(e, 'MMM D, YYYY', locale)}`;
    }
    default:
      return formatCivil(c, fmt, locale);
  }
}

/** Compact "Range" summary for the popover footer. A single-day pick (same
 *  start/end, or only a start chosen) shows one date rather than a duplicate. */
function formatRangeSummary(pair: Pair, locale: string): string | null {
  const f = (c: Civil) => formatCivil(c, 'MMM D, YYYY', locale);
  if (pair.start && pair.end) {
    return compareDate(pair.start, pair.end) === 0 ? f(pair.start) : `${f(pair.start)} – ${f(pair.end)}`;
  }
  if (pair.start) return f(pair.start);
  if (pair.end) return f(pair.end);
  return null;
}

function formatPair(pair: Pair, mode: string, fmt: string, locale: string, firstDayOfWeek: number): string {
  if (!pair.start && !pair.end) return '';
  if (RANGE_MODES.has(mode)) {
    const s = pair.start ? formatSingle(pair.start, mode, fmt, locale, firstDayOfWeek) : '…';
    const e = pair.end ? formatSingle(pair.end, mode, fmt, locale, firstDayOfWeek) : '…';
    return `${s} – ${e}`;
  }
  return pair.start ? formatSingle(pair.start, mode, fmt, locale, firstDayOfWeek) : '';
}
